import { useEffect, useRef } from 'react'

import { useLocation } from '@redwoodjs/router'

import {
  ensureProgrammaticFocusTarget,
  getMainContentElement,
  getPageHeadingText,
  getRouteAnnouncementFocusTarget,
  isRouteAnnouncementReady,
  resolveRouteAnnouncementText,
} from './use-route-announcement.util'

/** ID of the visually hidden live region that announces route changes. */
export const ROUTE_ANNOUNCER_ID = 'route-announcer'

const ROUTE_ANNOUNCEMENT_DELAY_MS = 50
const ROUTE_ANNOUNCEMENT_POLL_INTERVAL_MS = 50
const ROUTE_ANNOUNCEMENT_MAX_ATTEMPTS = 12
const ROUTE_ANNOUNCEMENT_HEADING_GRACE_ATTEMPTS = 8

const announceRouteText = (text: string) => {
  const announcer = document.getElementById(ROUTE_ANNOUNCER_ID)
  if (!announcer) {
    return
  }

  announcer.textContent = ''
  requestAnimationFrame(() => {
    announcer.textContent = text
  })
}

/**
 * Announces SPA route changes to screen readers and moves keyboard focus into
 * the page heading after navigation. Skips the initial mount so the first page
 * load is not re-announced.
 */
export const useRouteAnnouncement = () => {
  const { pathname } = useLocation()
  const isFirstRender = useRef(true)
  const previousHeadingTextRef = useRef<string | null>(getPageHeadingText(getMainContentElement()))

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    let cancelled = false
    let pollTimeoutId: number | undefined
    const previousDocumentTitle = document.title
    let attempt = 0

    const completeRouteAnnouncement = () => {
      if (cancelled) {
        return
      }

      const mainContent = getMainContentElement()
      const announcementText = resolveRouteAnnouncementText({
        mainContent,
        documentTitle: document.title,
      })
      const focusTarget = mainContent ? getRouteAnnouncementFocusTarget(mainContent) : null
      const headingText = getPageHeadingText(mainContent)

      if (
        focusTarget?.tagName === 'H1' &&
        headingText &&
        !mainContent?.contains(document.activeElement)
      ) {
        ensureProgrammaticFocusTarget(focusTarget)
        focusTarget.focus({ preventScroll: false })
        previousHeadingTextRef.current = headingText
        return
      }

      announceRouteText(announcementText)

      if (mainContent && !mainContent.contains(document.activeElement)) {
        ensureProgrammaticFocusTarget(mainContent)
        mainContent.setAttribute('aria-label', announcementText)
        mainContent.focus({ preventScroll: false })
      }

      previousHeadingTextRef.current = headingText
    }

    const pollForRouteAnnouncement = () => {
      if (cancelled) {
        return
      }

      const mainContent = getMainContentElement()
      const headingText = getPageHeadingText(mainContent)
      const readyToAnnounce = isRouteAnnouncementReady({
        previousDocumentTitle,
        previousHeadingText: previousHeadingTextRef.current,
        documentTitle: document.title,
        headingText,
        attempt,
        maxAttempts: ROUTE_ANNOUNCEMENT_MAX_ATTEMPTS,
        headingGraceAttempts: ROUTE_ANNOUNCEMENT_HEADING_GRACE_ATTEMPTS,
      })

      if (!readyToAnnounce) {
        attempt += 1
        pollTimeoutId = window.setTimeout(
          pollForRouteAnnouncement,
          ROUTE_ANNOUNCEMENT_POLL_INTERVAL_MS
        )
        return
      }

      completeRouteAnnouncement()
    }

    const initialTimeoutId = window.setTimeout(
      pollForRouteAnnouncement,
      ROUTE_ANNOUNCEMENT_DELAY_MS
    )

    return () => {
      cancelled = true
      window.clearTimeout(initialTimeoutId)
      if (pollTimeoutId !== undefined) {
        window.clearTimeout(pollTimeoutId)
      }
    }
  }, [pathname])
}

export {
  ensureProgrammaticFocusTarget,
  getRouteAnnouncementFocusTarget,
} from './use-route-announcement.util'
